/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { PRIMARY_OWNER } from '../../constants/accountTypes';
import './settings.css';
import Features from './payments/Features';
import SubscriptionDetails from '../../components/SubscriptionDetails';
import { useMembershipName } from '../../hooks/useMembershipName';

const BTN_GHOST = 'inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition cursor-pointer';
const BTN_GREEN = 'inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] transition';
const BTN_RED = 'inline-flex items-center justify-center box-border h-10 px-4 bg-[#CC4525] text-white rounded-[8px] text-sm font-semibold hover:bg-[#a8391d] transition';

function SettingsTab() {
  const { activeWorkspace, setActiveWorkspace, member } = useContext(UserContext);
  const canDeleteWorkspace = member?.accountType === PRIMARY_OWNER;
  const [avatar, setAvatar] = useState('');
  const membershipName = useMembershipName(activeWorkspace.membership);

  useEffect(() => {
    (async () => {
      const response = await sendRequest({ method: 'get', url: API.WORKSPACE.getWorkspacePicture(activeWorkspace.id) });
      setAvatar(response.data.picture);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageUpload = async (event) => {
    const formData = new FormData();
    formData.append('avatar', event.target.files[0]);
    try {
      const response = await sendRequest({
        method: 'put',
        url: API.WORKSPACE.updateWorkspacePicture(activeWorkspace.id),
        data: formData,
      });
      if (response.success) {
        setActiveWorkspace({ ...activeWorkspace, picture: response.data.picture });
        setAvatar(response.data.picture);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('workspace picture update error:', e);
    }
  };

  const handleImageRemove = async () => {
    try {
      const response = await sendRequest({ method: 'delete', url: API.WORKSPACE.removeWorkspacePicture(activeWorkspace.id) });
      if (response.success) setAvatar('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('workspace picture remove error:', e);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Workspace Logo */}
      <section>
        <h4 className="text-base font-semibold text-ink-900 mb-0.5">Workspace Logo</h4>
        <p className="text-sm text-ink-500 mb-4">Upload a logo to represent your workspace</p>
        <div className="flex flex-col items-start gap-4">
          <div
            className="flex items-center justify-center flex-shrink-0 overflow-hidden rounded-[12px] border border-rule"
            style={{ width: 120, height: 120, background: '#f1f2f5', borderStyle: 'solid' }}
          >
            {avatar
              ? <img src={avatar} alt="Workspace logo" className="w-full h-full object-contain" />
              : <span className="text-xs text-ink-500 text-center px-2">No logo uploaded</span>}
          </div>
          <div className="flex items-center gap-2">
            <input type="file" accept="image/*" onChange={handleImageUpload} id="workspace-logo-file" className="hidden" />
            <button
              type="button"
              onClick={() => document.getElementById('workspace-logo-file').click()}
              className={BTN_GHOST}
              style={{ borderStyle: 'solid', height: 40, boxSizing: 'border-box' }}
            >
              {avatar ? 'Change Logo' : 'Upload Logo'}
            </button>
            {avatar && (
              <button type="button" onClick={handleImageRemove} className={BTN_RED} style={{ height: 40, boxSizing: 'border-box' }}>Remove</button>
            )}
          </div>
        </div>
      </section>

      <div style={{ height: 1, background: '#e0e1e6' }} />

      {/* Payment & Billing */}
      <section>
        <h4 className="text-base font-semibold text-ink-900 mb-3">Payment &amp; Billing</h4>
        <SubscriptionDetails
          workspaceId={activeWorkspace.id}
          fallbackContent={(
            <>
              <p className="text-sm text-ink-700 mb-1">
                {'Your workspace is on the '}
                <strong>{`${membershipName} Plan`}</strong>
              </p>
              <p className="text-sm text-ink-700 mb-3">
                {'Upgrade to the '}
                <strong>Basic, Team, Professional</strong>
                {' or '}
                <strong>Enterprise</strong>
                {' plan for unlimited access to features, including:'}
              </p>
              <Features />
            </>
          )}
        />
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Link to={{ pathname: '/admin/settings/payments', state: { selectedTab: 0 } }}>
            <button type="button" className={BTN_GHOST} style={{ borderStyle: 'solid', height: 40, boxSizing: 'border-box' }}>Billing Settings</button>
          </Link>
          <Link to={{ pathname: '/admin/settings/payments', state: { selectedTab: 1 } }}>
            <button type="button" className={BTN_GREEN} style={{ height: 40, boxSizing: 'border-box' }}>Upgrade Your Workspace</button>
          </Link>
        </div>
      </section>

      <div style={{ height: 1, background: '#e0e1e6' }} />

      {/* Danger zone */}
      <section className="rounded-[12px] p-4" style={{ border: '1px solid #f3cfc6', background: '#fdf6f4' }}>
        <h4 className="text-base font-semibold mb-0.5" style={{ color: '#a8391d' }}>Delete Workspace</h4>
        <p className="text-sm text-ink-700 mb-4">Deleting a Shoot workspace can&apos;t be undone.</p>
        <button
          type="button"
          disabled={!canDeleteWorkspace}
          title={canDeleteWorkspace ? undefined : 'Only the workspace owner can delete the workspace'}
          className={`${BTN_RED} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          Delete Workspace
        </button>
        {!canDeleteWorkspace && (
          <p className="text-xs text-ink-500 mt-2">Only the workspace owner can delete the workspace.</p>
        )}
      </section>
    </div>
  );
}

export default SettingsTab;
